import { observable, computed } from "mobx";
import { isEmpty } from "lodash";
import {
  GetCustomerContractEditorData,
  HighHolidayFragment,
  SimpleCustomerCardFragment,
  SubmitCustomerContractForm,
  CustomerContracts,
  CustomerContractCompany
} from "@graphql";
import { FormStore } from "@modules/formx";
import { Navigation } from "@navigation";
import { store } from "@store";
import { isAllEmpty } from "@bonliva-crm/common";
import {
  CustomerContractContactPersonInput,
  CustomerContractFormInput
} from "./CustomerContractFormInput";
import { CustomerContractProfileLocation } from "../locations/CustomerContractProfileLocation";

export class CustomerContractEditorStore {
  @observable isLoading = true;
  @observable error = null;
  highHolidays: HighHolidayFragment[] = [];

  form = new FormStore<CustomerContractFormInput>({
    classValidatorClassType: CustomerContractFormInput,
    initialValues: new CustomerContractFormInput(),
    debounceValidationMs: 250,
    exposeAs: "customerContractEditorStore",
    onSubmit: () => this.submit(),
    onSetValue: ({ name, value }) => {
      if (name === "company") {
        this.form.values.professions = [];
      }
      if (name === "contractProlongation" && !value) {
        this.form.values.contractPriceAdjustmentDate = null;
        this.form.values.prolongationComment = "";
        this.form.values.adjustmentComment = "";
      }

      if (name === "overtime" && !value) {
        this.form.values.overtimeNote = "";
      }

      if (name === "possibleInvoiceForAccommodation" && !value) {
        this.form.values.possibleInvoiceForAccommodationNote = "";
      }

      if (name === "possibleInvoiceForTravel" && !value) {
        this.form.values.possibleInvoiceForTravelNote = "";
      }

      if (name === "overtimeOption" && !value) {
        this.form.values.overtimeOptionOBNote = "";
      }

      if (name === "otherOption" && !value) {
        this.form.values.otherOptionNote = "";
      }
    }
  });

  constructor(public customerContractId: string | null, public navigation: Navigation) {
    this.init();
    (window as any).customerContractEditorStore = this;
  }

  get isNew() {
    return !this.customerContractId;
  }

  @computed get professions() {
    if (this.form?.values?.company === CustomerContractCompany.Bonliva_Care) {
      return store().cache.bcProfessions;
    }
    return store().cache.professions;
  }

  get generalTabHasErrors() {
    const { errors } = this.form;
    return !isAllEmpty(
      errors.contractEnd,
      errors.introductionInfo,
      errors.contractNumber,
      errors.contractPriceAdjustmentDate,
      errors.contractProlongation,
      errors.contractStart,
      errors.currentContractEnd,
      errors.orgCustomers,
      errors.professions,
      errors.typeOfAgreement,
      errors.contactPerson
    );
  }

  get isProlongationDisabled() {
    return !this.form.values.contractProlongation;
  }

  close = () => {
    this.navigation.goBack();
  };

  submit = () => {
    const formData = this.form.values.getPlain();
    formData.estimatedContractValue = formData?.estimatedContractValue
      ? Number(formData.estimatedContractValue)
      : null;
    formData.dispenseDeadline =
      formData?.company === CustomerContractCompany.Bonliva_Care
        ? Number(formData.dispenseDeadline)
        : null;

    return SubmitCustomerContractForm.func(
      {
        customerContractId: this.customerContractId,
        data: formData
      },
      { refetchQueries: [CustomerContracts.OperationName] }
    ).then(({ data }) => {
      // OK and navigate
      if (isEmpty(data.submitCustomerContractForm.errors)) {
        this.navigation.unBlock("customer_contract_form");
        this.navigation.navigate(
          new CustomerContractProfileLocation(data.submitCustomerContractForm.value._id)
        );

        return;
      }

      store().snackbars.error(
        `Cannot save form\nReason: ${data.submitCustomerContractForm.errors.join("\n")}`
      );

      // if (!isEmpty(data.submitCustomerContractForm?.ValidationErrors)) {
      //   this.form.setErrors(data.submitCustomerContractForm.ValidationErrors);
      // }
    });
  };

  setOrgCustomers = (customers: SimpleCustomerCardFragment[]) => {
    this.form.values.orgCustomers = customers.map((x) => ({
      Id: x._id,
      Name: x.Name,
      Type: x.Type
    }));
    this.form.validate();
  };

  removeOrgCustomer = (id: string) => {
    this.form.values.orgCustomers = this.form.values.orgCustomers.filter(
      (x) => x.Id !== id
    );
    this.form.validate();
  };

  setDispenseDeadline = (val: string) => {
    const regex = /^(?:[1-9]|[1-4][0-9]|5[0-2])$/;
    if (regex.test(val) || val === "") {
      this.form.values.dispenseDeadline = val;
      this.form.validate();
    }
  };

  handleAddContact = () => {
    this.form.push("contactPerson", new CustomerContractContactPersonInput());
    this.form.validate();
  };

  handleRemoveContact = (index) => {
    this.form.removeByIndex("contactPerson", index);
    this.form.validate();
  };

  private init() {
    GetCustomerContractEditorData.func(
      { id: this.customerContractId, isNew: !this.customerContractId },
      { fetchPolicy: "network-only" }
    )
      .then(({ data }) => {
        if (data.customerContractById) {
          const form = CustomerContractFormInput.CreateFromFragment(
            data.customerContractById
          );
          // NP contracts always invoice the NP way — show checked (the checkbox
          // is rendered disabled for them).
          if (form.isNationalProcurement) {
            form.useNationalProcurementInvoicing = true;
          } else {
            form.useNationalProcurementInvoicing = Boolean(
              form.useNationalProcurementInvoicing
            );
          }
          this.form.reset(form);
        } else {
          this.form.reset(this.form.values);
        }
      })
      .catch((err) => {
        console.log(err);
        store().snackbars.error(`Cannot load data`);
      })
      .finally(() => {
        this.isLoading = false;
      });
  }
}
